// Dev-only browser tester for the headless render-route. Attaches
// window.testChartExport so /_render/chart can be exercised from the console:
//   await testChartExport()                                 // default bars fixture
//   await testChartExport({ params: { chartMode: 'percents' } })
//   await testChartExport({ chartType: 'dummy' })
//   await testChartExport({ chartType: 'nope' })            // → Unknown chartType
//   await testChartExport({ data: realPayload, params: { chartMode: 'respondents' } })
//
// Loaded only outside production (see registerCharts.ts) — never a runtime dependency.
// Per-chart-type fixtures live in ./constants.ts.
// Spec: .aidev/tasks/headless-charts-sdk-spike/docs/tasks/02-highcharts-bars.md §6

/* eslint-disable no-console */

import {
  DEFAULT_BARS_DATA,
  DEFAULT_VK_INFOGRAPHIC_DATA,
  FIXTURES
} from '@aytm/app/stats/page/render-shell/test-utils/constants'

type TestChartExportOpts = {
  chartType?: string
  requestId?: string
  data?: any
  params?: Record<string, any>
  show?: boolean
}

async function testChartExport(opts: TestChartExportOpts = {}) {
  const win = window as any
  const { chartType = 'cb', show = true } = opts
  const requestId = opts.requestId || `test-${Date.now()}`
  const fixture = FIXTURES[chartType]
  let data = opts.data ?? fixture?.data ?? DEFAULT_BARS_DATA
  // vk/wd infographic mode swaps to the richer infographic answer fixture.
  if (!opts.data && (chartType === 'vk' || chartType === 'wd') && opts.params?.infographic) {
    data = DEFAULT_VK_INFOGRAPHIC_DATA
  }
  // The page watchdog is pulled under this helper's own 18s net, so a stuck chart reports
  // through the event channel — with its state snapshot — instead of as a bare local timeout.
  // 15s clears the worst legitimate path (8s bounded layout + ~7s font budget) with margin —
  // a timer that expires while the sync layout still holds the thread would fire ahead of the
  // real success and suppress it.
  const params = {
    chartMode: 'respondents',
    sortOrder: 'iasc',
    watchdogMs: 15000,
    ...fixture?.params,
    ...opts.params,
    chartType
  }

  if (typeof win.setChartExportData !== 'function') {
    console.error('setChartExportData is unavailable — open /_render/chart first')
    return
  }

  // Subscribe before injecting so a fast chart can't fire before we listen.
  const detail: any = await new Promise(resolve => {
    const handler = (e: any) => {
      if (e.detail.requestId !== requestId) return
      window.removeEventListener('aytm:chartReady', handler)
      resolve(e.detail)
    }
    window.addEventListener('aytm:chartReady', handler)
    win.setChartExportData(data, params, requestId)
    setTimeout(() => {
      window.removeEventListener('aytm:chartReady', handler)
      resolve({ requestId, error: 'timeout 18s' })
    }, 18000)
  })

  console.log('chartReady:', detail)
  if (detail.error) {
    console.error('❌', detail.error)
    return detail
  }

  const pngs: string[] = await win.getChartExportPng()
  const dims = await Promise.all(
    pngs.map(
      src =>
        new Promise<string>(resolve => {
          const img = new Image()
          img.onload = () => resolve(`${img.width}x${img.height}`)
          img.src = src
        })
    )
  )
  const valid = pngs.every(p => p.startsWith('data:image/png'))
  console.log(`✅ ${pngs.length} PNG, valid:${valid}, dims:`, dims)

  // Pin the rendered PNG(s) to the corner so the result is visually verifiable.
  if (show) {
    document.querySelectorAll('[data-chart-test]').forEach(n => n.remove())
    pngs.forEach((src, i) => {
      const img = new Image()
      img.src = src
      img.dataset.chartTest = '1'
      img.style.cssText = `position:fixed;top:${
        8 + i * 80
      }px;right:8px;max-width:320px;border:2px solid lime;z-index:9999;background:#fff`
      document.body.appendChild(img)
    })
  }

  return { detail, pngs, dims }
}

;(window as any).testChartExport = testChartExport
